import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Card, message, notification, Image ,Upload, Radio} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import Button from "../../../../components/common/button";
import ImageUpload from "../../../../components/common/imageUpload";
import {
  addServiceCategory,
  updateServiceCategory,
  addSubserviceCategory,
  updateSubserviceCategory,
  uploadImage,
  getBusinessServices,
} from "../../../../api/service";
import Close from "./../../../../assets/icons/close.svg";
import colors from "../../../../share/constants/constants";
import Checkbox from "../../../../components/common/checkBox";
import Dropdown from "../../../../components/common/dropdown";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategories } from "../../../../store/slices/categorySlice";
import { fetchSubCategories } from "../../../../store/slices/subCategorySlice";
import { fetchBusiness, reset } from "../../../../store/slices/businessSlice";

const AddTypeCard = ({
  visible,
  onClose,
  editValues,
  category,
  subservice,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const [imageFile, setImageFile] = useState(editValues?.image || null);
  const [categoryName, setCategoryName] = useState(editValues?.name || "");
  const [categoryTitle, setCategoryTitle] = useState(editValues?.title || "");
  const [isDisplay, setIsDisplay] = useState(editValues?.isDisplay);
  const [subServiceImageFile, setSubServiceImageFile] = useState(null);
  const [serviceCategory, setServiceCategory] = useState(
    editValues?.serviceCategoryId || ""
  );
  const [subCategoryName, setSubCategoryName] = useState({
    name: "",
    title: "",
    image: "",
  });
  // Banner state
  const [bannerImageFile, setBannerImageFile] = useState(editValues?.banner?.imageUrl || null);
  const [bannerClickAction, setBannerClickAction] = useState(editValues?.banner?.clickAction || null);
  const [selectedBannerVendorId, setSelectedBannerVendorId] = useState(editValues?.banner?.vendorId || null);
  const [selectedBannerServiceId, setSelectedBannerServiceId] = useState(editValues?.banner?.serviceId || null);
  const [bannerLink, setBannerLink] = useState(editValues?.banner?.link || "");
  const [bannerServices, setBannerServices] = useState([]);
  const [isBannerServiceLoading, setIsBannerServiceLoading] = useState(false);

  const categories = useSelector((state) => {
    return state?.category?.res;
  });

  const subCategories = useSelector((state) => {
    return state?.subCategory?.res || [];
  });

  const businessNames = useSelector((state) => state?.business?.res?.items || []);
  const isBusinessLoading = useSelector((state) => state?.business?.isLoading);

  useEffect(() => {
    if (editValues) {
      form.setFieldsValue({
        name: editValues.name,
        title: editValues.title,
      });
      if (editValues?.serviceCategoryId) {
        form.setFieldsValue({
          serviceCategoryId: editValues.serviceCategoryId,
        });
      }
      if (editValues?.banner) {
        form.setFieldsValue({
          bannerClickAction: editValues.banner.clickAction,
          bannerVendorId: editValues.banner.vendorId,
          bannerServiceId: editValues.banner.serviceId,
          bannerLink: editValues.banner.link,
        });
        setBannerClickAction(editValues.banner.clickAction);
        setSelectedBannerVendorId(editValues.banner.vendorId);
        setSelectedBannerServiceId(editValues.banner.serviceId);
        setBannerLink(editValues.banner.link);
        setBannerImageFile(editValues.banner.imageUrl);
        // Load services if vendor is selected (for editing)
        if (editValues.banner.vendorId) {
          getBannerServicesByBusinessId(editValues.banner.vendorId, 1);
        }
      }
      setCategoryName(editValues.name);
      setCategoryTitle(editValues.title);
      setImageFile(editValues.image);
      setServiceCategory(editValues?.serviceCategoryId);
      setIsDisplay(editValues?.isDisplay);
    }
  }, [editValues, form]);

  useEffect(() => {
    dispatch(fetchCategories());
    if (subservice) {
      dispatch(fetchSubCategories());
      // Fetch businesses for banner vendor selection
      const payload = {
        pageNumber: 1,
        itemPerPage: 50,
      };
      dispatch(fetchBusiness(payload));
    }
  }, [dispatch, subservice]);

  const getBannerServicesByBusinessId = async (businessId, page) => {
    setIsBannerServiceLoading(true);
    try {
      const res = await getBusinessServices({
        pageNumber: page,
        pageSize: 50,
        businessId: businessId,
      });
      if (res) {
        setBannerServices((prevServices) => [...prevServices, ...res?.items]);
      }
      // Set selected service if editing
      if (res && editValues?.banner?.serviceId) {
        setSelectedBannerServiceId(editValues.banner.serviceId);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setIsBannerServiceLoading(false);
    }
  };

  const handleBannerVendorChange = async (vendorId) => {
    setSelectedBannerVendorId(vendorId);
    setSelectedBannerServiceId(null);
    setBannerServices([]);
    form.setFieldsValue({ bannerServiceId: null });
    if (vendorId) {
      await getBannerServicesByBusinessId(vendorId, 1);
    }
  };

  const handleBannerServiceChange = (serviceId) => {
    setSelectedBannerServiceId(serviceId);
  };

  const loadMoreBannerServiceData = async (page) => {
    if (selectedBannerVendorId) {
      await getBannerServicesByBusinessId(selectedBannerVendorId, page);
    }
  };

  const fetchBusinessByName = (payload) => {
    if (payload?.pageNumber === 1 && payload?.text) {
      dispatch(reset());
    }
    dispatch(fetchBusiness(payload));
  };

  const onBannerDropdownClose = () => {
    dispatch(reset());
    const payload = {
      pageNumber: 1,
      itemPerPage: 50,
    };
    dispatch(fetchBusiness(payload));
  };

 const beforeUpload = (file) => {
  const isJpgOrPng =
    file.type === "image/jpeg" ||
    file.type === "image/png" ||
    file.type === "image/webp";

  const isLt500KB = file.size / 1024 <= 1000; // 500 KB limit

  if (!isJpgOrPng) {
    message.error({
      content: 'You can only upload JPG, PNG, or WebP file formats!',
      style: { marginTop: '20px' },
      duration: 4,
    });
  }

  if (!isLt500KB) {
    const actualSize = (file.size / 1024).toFixed(2); // size in KB
    message.error({
      content: `Image size (${actualSize} KB) exceeds the 500 KB limit. Please select a smaller one.`,
      style: { marginTop: '20px' },
      duration: 4,
    });
  }

  return { isJpgOrPng, isLt500KB };
};

const handleImageUpload = async (info) => {
  const { file } = info;
  const validImage = beforeUpload(file);
  if (validImage.isJpgOrPng && validImage.isLt500KB) {
    await handleChangeAndUploadImage(info)
  }
};

const handleChangeAndUploadImage = async (event) => {
  try {
    if (event.file) {
      const formData = new FormData();
      formData.append('image', event.file);
      const response = await uploadImage(formData);
      if (response) {
        setImageFile(response.url);
        setSubServiceImageFile(response.url);
        message.success("Image upload successful");
      }
    }
  } catch (error) {
    console.log(error)
    message.error("Error while uploading image");
  }
};

const handleBannerImageUpload = async (info) => {
  const { file } = info;
  const validImage = beforeUpload(file);
  if (validImage.isJpgOrPng && validImage.isLt500KB) {
    try {
      if (file) {
        const formData = new FormData();
        formData.append('image', file);
        const response = await uploadImage(formData);
        if (response) {
          setBannerImageFile(response.url);
          form.setFieldValue("bannerImage", response.url);
          message.success("Banner image upload successful");
        }
      }
    } catch (error) {
      console.log(error)
      message.error("Error while uploading banner image");
    }
  }
};


  const handleModalClose = () => {
    form.resetFields();
    setImageFile(null);
    setCategoryName("");
    setCategoryTitle("");
    setServiceCategory("");
    setIsDisplay(false);
    setBannerImageFile(null);
    setBannerClickAction(null);
    setSelectedBannerVendorId(null);
    setSelectedBannerServiceId(null);
    setBannerLink("");
    setBannerServices([]);
    onClose();
  };

  const onFinish = async (values) => {
    if (
      category &&
      !editValues &&
      (!categoryName ||
        !imageFile ||
        !subCategoryName.name ||
        !subCategoryName.title ||
        !subServiceImageFile)
    ) {
      message.error("Please upload image!");
      return;
    }

    const payload = {
      name: categoryName || values.name,
      image: imageFile,
      ...(subservice && {
        title: categoryTitle || values.title,
        isDisplay: isDisplay || false,
        serviceCategoryId: serviceCategory,
        ...(bannerImageFile && {
          banner: {
            imageUrl: bannerImageFile,
            clickAction: bannerClickAction,
            ...(bannerClickAction === "service" && selectedBannerVendorId && selectedBannerServiceId && {
              vendorId: selectedBannerVendorId,
              serviceId: selectedBannerServiceId,
            }),
            ...(bannerClickAction === "link" && bannerLink && {
              link: bannerLink,
            }),
          },
        }),
      }),
    };

    try {
      setLoader(true);
      let res;
      if (editValues) {
        res = category
          ? await updateServiceCategory(editValues._id, payload)
          : await updateSubserviceCategory(editValues._id, payload);
        if (res) {
          handleModalClose();
          notification.success({
            title: "Success",
            description: "Updated successfully!",
          });
        }
      } else {
        if (category) {
          const categoryRes = await addServiceCategory(payload);
          const subServicePayload = {
            name: subCategoryName.name,
            title: subCategoryName.title,
            image: subServiceImageFile,
            serviceCategoryId: categoryRes._id,
          };
          if (categoryRes) {
            const subServiceRes = await addSubserviceCategory(
              subServicePayload
            );
            if (subServiceRes) {
              handleModalClose();
              notification.success({
                title: "Success",
                description: "Category added successfully!",
              });
            }
          }
        } else {
          const subRes = await addSubserviceCategory(payload);
          if (subRes) {
            handleModalClose();
            notification.success({
              title: "Success",
              description: "Sub Category added successfully!",
            });
          }
        }
      }
    } catch (error) {
      message.error("An error occurred!");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    dispatch(fetchCategories());
    // dispatch(fetchSubServiceCategories());
  }, []);

  return (
    <Modal
      open={visible}
      footer={null}
      className={subservice ? "top-5" : "top-1"}
      width="436px"
      onCancel={handleModalClose}
      closeIcon={<img src={Close} alt="Close" className="w-3.2" />}
    >
      <Card>
        <h2 className="text-center mb-4">
          {editValues ? "Edit" : "Add"} {category ? "Service" : "Sub-Service"}{" "}
          Category
        </h2>
        <Form
          form={form}
          onFinish={onFinish}
          initialValues={editValues}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label={category ? "Category Name" : "Sub Service Name"}
            rules={[{ required: true, message: "field is required" }]}
          >
            <div>
              <Input
                placeholder={category ? "Category Name" : "Sub Service Name"}
                className="w-full h-5.4 !rounded-xl border-unset"
                onChange={(e) => setCategoryName(e.target.value)}
                value={categoryName}
              />
            </div>
          </Form.Item>
          <Form.Item
          name="image"
          rules={[
            {
              required: true,
              message: "Please upload an image",
            },
          ]}
          >
           <Upload
            listType="picture-card"
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleImageUpload}
            accept="image/jpeg,image/png,image/webp"
           >
              <div>
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>Upload</div>
              </div>
             </Upload>
            </Form.Item>
          {imageFile && (
            <div className="text-center my-4">
              <Image
                src={imageFile}
                width={120}
                height={75}
                preview={true}
                alt="attachment"
                className="rounded object-cover"
              />
            </div>
          )}

          {category && !editValues && (
            <>
              <h4 className="mb-4">
                * Please add at least one sub-service category to create a
                service category
              </h4>
              <Form.Item
                name="subServiceCategoryName"
                label="Sub Service Category Name"
                className="w-full"
                rules={[{ required: true, message: "field is required" }]}
              >
                <Input
                  placeholder={"Sub Service Category Name"}
                  onChange={(e) => {
                    setSubCategoryName({
                      ...subCategoryName,
                      name: e.target.value,
                    });
                  }}
                  className="h-5  focus:border-primaryBlue hover:border-primaryBlue"
                />
              </Form.Item>
              <Form.Item
                name="subServiceCategoryTitle"
                label="Sub Service Category Title (shown on landing page)"
                className="w-full"
                rules={[{ required: true, message: "field is required" }]}
              >
                <Input
                  placeholder={
                    "Sub Service Category Title (shown on landing page)"
                  }
                  onChange={(e) => {
                    setSubCategoryName({
                      ...subCategoryName,
                      title: e.target.value,
                    });
                  }}
                  className="h-5 focus:border-primaryBlue hover:border-primaryBlue"
                />
              </Form.Item>
          <Upload
                  listType="picture-card"
                  showUploadList={false}
                  beforeUpload={() => false}
                  onChange={handleImageUpload}
                  accept="image/jpeg,image/png,image/webp"
                >
            <div>
               <PlusOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          </Upload>
              {subServiceImageFile && (
                <div className="text-center my-4">
                  <Image
                    src={subServiceImageFile}
                    width={120}
                    height={75}
                    preview={true}
                    alt="attachment"
                    className="rounded object-cover"
                  />
                </div>
              )}
            </>
          )}

          {subservice && (
            <Form.Item
              name="title"
              label={
                category
                  ? "Category Title"
                  : "Sub Service Title (Title is shown on landing page cards)"
              }
              rules={[{ required: true, message: "field is required" }]}
            >
              <div>
                <Input
                  placeholder={
                    category
                      ? "Category Title"
                      : "Sub Service Title (Title is shown on landing page cards)"
                  }
                  className="w-full h-5.4 !rounded-xl border-unset"
                  onChange={(e) => setCategoryTitle(e.target.value)}
                  value={categoryTitle}
                />
              </div>
            </Form.Item>
          )}
          {subservice ? (
            <>
              <Form.Item
                name="serviceCategoryId"
                label="Choose a Category"
                className="w-full"
                rules={[{ required: true, message: "field is required" }]}
              >
                <Dropdown
                  className={"h-5 !rounded-lg"}
                  placeholder="Choose a Category"
                  defaultValue={editValues?.serviceCategoryId}
                  loadMoreData={(e) => {}}
                  data={categories}
                  onChange={(e) => {
                    setServiceCategory(e);
                  }}
                />
              </Form.Item>
              <Form.Item name="isDisplay" className="w-full">
                <Checkbox
                  description={"Show on Landing Page as a Card?"}
                  defaultValue={editValues?.isDisplay}
                  color={"black"}
                  onChange={(e) => {
                    setIsDisplay(e);
                  }}
                />
              </Form.Item>

              {/* Banner Section */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="mb-3 text-base font-semibold">Banner Settings</h3>
                
                <Form.Item
                  name="bannerImage"
                  label="Banner Image"
                  className="w-full"
                >
                  <Upload
                    listType="picture-card"
                    showUploadList={false}
                    beforeUpload={() => false}
                    onChange={handleBannerImageUpload}
                    accept="image/jpeg,image/png,image/webp"
                  >
                    {bannerImageFile ? (
                      <img
                        src={bannerImageFile}
                        alt="banner"
                        style={{ width: "100%" }}
                      />
                    ) : (
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>Upload Banner</div>
                      </div>
                    )}
                  </Upload>
                </Form.Item>

                {bannerImageFile && (
                  <Form.Item
                    name="bannerClickAction"
                    label="Banner Click Action"
                    className="w-full"
                    rules={[
                      {
                        required: true,
                        message: "Please select a click action",
                      },
                    ]}
                  >
                    <Radio.Group
                      onChange={(e) => {
                        setBannerClickAction(e.target.value);
                        setSelectedBannerVendorId(null);
                        setSelectedBannerServiceId(null);
                        setBannerServices([]);
                        setBannerLink("");
                        form.setFieldsValue({
                          bannerVendorId: null,
                          bannerServiceId: null,
                          bannerLink: "",
                        });
                      }}
                      value={bannerClickAction}
                    >
                      <Radio value="service">Vendor Service</Radio>
                      <Radio value="link">Third Party Link</Radio>
                    </Radio.Group>
                  </Form.Item>
                )}

                {bannerClickAction === "service" && bannerImageFile && (
                  <>
                    <Form.Item
                      name="bannerVendorId"
                      label="Select Vendor"
                      className="w-full"
                      rules={[
                        {
                          required: true,
                          message: "Please select a vendor",
                        },
                      ]}
                    >
                      <Dropdown
                        className={"h-5 !rounded-lg"}
                        placeholder="Choose a Vendor"
                        defaultValue={selectedBannerVendorId}
                        loadMoreData={(e, itemPerPage, text) => {
                          const payload = {
                            pageNumber: e,
                            itemPerPage: itemPerPage || 50,
                          };
                          if (text) {
                            payload.text = text;
                            payload.isLoadMore = true;
                          }
                          fetchBusinessByName(payload);
                        }}
                        data={businessNames}
                        onChange={handleBannerVendorChange}
                        loader={isBusinessLoading}
                        isBusiness={true}
                        fetchData={fetchBusinessByName}
                        onDropDownClose={onBannerDropdownClose}
                      />
                    </Form.Item>
                    {selectedBannerVendorId && (
                      <Form.Item
                        name="bannerServiceId"
                        label="Select Service"
                        className="w-full"
                        rules={[
                          {
                            required: true,
                            message: "Please select a service",
                          },
                        ]}
                      >
                        <Dropdown
                          className={"h-5 !rounded-lg"}
                          placeholder="Choose a Service"
                          defaultValue={selectedBannerServiceId}
                          loadMoreData={(e) => {
                            loadMoreBannerServiceData(e);
                          }}
                          data={bannerServices}
                          onChange={handleBannerServiceChange}
                          loader={isBannerServiceLoading}
                        />
                      </Form.Item>
                    )}
                  </>
                )}

                {bannerClickAction === "link" && bannerImageFile && (
                  <Form.Item
                    name="bannerLink"
                    label="Third Party Link"
                    className="w-full"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a link",
                      },
                      {
                        type: "url",
                        message: "Please enter a valid URL",
                      },
                    ]}
                  >
                    <Input
                      placeholder="https://example.com"
                      className="w-full h-5.4 !rounded-xl border-unset"
                      onChange={(e) => setBannerLink(e.target.value)}
                      value={bannerLink}
                    />
                  </Form.Item>
                )}
              </div>
            </>
          ) : (
            ''
          )}

          <Form.Item className="text-center mt-2">
            <Button
              title={editValues ? "Update" : "Save"}
              color={colors?.primaryPurple}
              htmlType="submit"
              loading={loader}
              className="w-full h-5.4"
            />
          </Form.Item>
        </Form>
      </Card>
    </Modal>
  );
};

export default AddTypeCard;
